import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map as KakaoMap, MapMarker, useKakaoLoader } from 'react-kakao-maps-sdk';
import { getFavorites, addFavorite, deleteFavorite } from '../../apis/api';
import { getMyPlans, updateAiPlan } from '../../apis/aiApi';
import styles from './MapPage.module.css';

// Assets
import starMarkerIcon from '../../assets/pic/지도마커/즐겨찾기.png';
import visitedMarkerIcon from '../../assets/pic/지도마커/다녀온장소.png';

const MapPage = () => {
    useKakaoLoader({ appkey: process.env.REACT_APP_KAKAOMAP_KEY, libraries: ['services'] });
    const navigate = useNavigate();
    const [favorites, setFavorites] = useState([]);
    const [plans, setPlans] = useState([]);
    const [resolvedPlaces, setResolvedPlaces] = useState([]); // Places with lat/lng
    const [loading, setLoading] = useState(true);
    const [selectedPlace, setSelectedPlace] = useState(null);

    // Initial Data Fetch
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [favData, plansData] = await Promise.all([
                    getFavorites(),
                    getMyPlans()
                ]);
                setFavorites(favData || []);
                setPlans(plansData || []);
            } catch (error) {
                console.error("Failed to load map data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Combine data into unique locations (Raw)
    const rawPlaces = useMemo(() => {
        const placeMap = new Map(); // Native JS Map
        const getKey = (name, addr) => `${name}|${addr}`;

        // Process Favorites
        favorites.forEach(fav => {
            const key = getKey(fav.storeName, fav.address);
            if (!placeMap.has(key)) {
                placeMap.set(key, {
                    key,
                    name: fav.storeName,
                    address: fav.address,
                    lat: fav.latitude || 0,
                    lng: fav.longitude || 0,
                    category: fav.category,
                    imageUrl: fav.imageUrl,
                    rating: fav.rating,
                    favoriteData: fav,
                    plansData: []
                });
            } else {
                const p = placeMap.get(key);
                p.favoriteData = fav;
            }
        });

        // Process Plans
        plans.forEach(plan => {
            const realPlan = plan.plan || plan;
            if (!realPlan.schedule) return;

            realPlan.schedule.forEach((day, dayIdx) => {
                if (!day.places) return;
                day.places.forEach((place, placeIdx) => {
                    const name = place.placeName || place.name;
                    const addr = place.address;
                    if (!name || !addr) return;

                    const key = getKey(name, addr);

                    if (!placeMap.has(key)) {
                        placeMap.set(key, {
                            key,
                            name: name,
                            address: addr,
                            lat: place.latitude || place.y || 0,
                            lng: place.longitude || place.x || 0,
                            plansData: []
                        });
                    }

                    const p = placeMap.get(key);
                    if (!p.lat && (place.latitude || place.y)) {
                        p.lat = place.latitude || place.y;
                        p.lng = place.longitude || place.x;
                    }

                    p.plansData.push({
                        planId: plan.id,
                        planTitle: realPlan.title,
                        day: dayIdx + 1,
                        placeIndex: placeIdx,
                        originalPlan: plan,
                        isChecked: place.isChecked
                    });
                });
            });
        });
        return Array.from(placeMap.values());
    }, [favorites, plans]);

    // Geocoding Effect
    useEffect(() => {
        if (loading) return;

        let cancelled = false;

        const geocode = async () => {
            const toGeocode = rawPlaces.filter(p => !p.lat || !p.lng);
            const alreadyresolved = rawPlaces.filter(p => p.lat && p.lng);

            if (toGeocode.length === 0) {
                if (!cancelled) setResolvedPlaces(alreadyresolved);
                return;
            }

            if (!window.kakao || !window.kakao.maps || !window.kakao.maps.services) {
                if (!cancelled) setResolvedPlaces(alreadyresolved);
                return;
            }

            const geocoder = new window.kakao.maps.services.Geocoder();

            const promises = toGeocode.map(p => new Promise(resolve => {
                geocoder.addressSearch(p.address, (result, status) => {
                    if (status === window.kakao.maps.services.Status.OK && result[0]) {
                        resolve({ ...p, lat: result[0].y, lng: result[0].x });
                    } else {
                        resolve(p);
                    }
                });
            }));

            const results = await Promise.all(promises);
            if (!cancelled) {
                setResolvedPlaces([...alreadyresolved, ...results].filter(p => p.lat && p.lng));
            }
        };

        geocode();
        return () => { cancelled = true; };
    }, [rawPlaces, loading /*, window.kakao */]);

    // Handlers
    const handleToggleFavorite = async (place) => {
        try {
            if (place.favoriteData) {
                await deleteFavorite(place.favoriteData.id);
                setFavorites(prev => prev.filter(f => f.id !== place.favoriteData.id));
                place.favoriteData = null;
            } else {
                const newFav = {
                    storeName: place.name,
                    address: place.address,
                    category: place.category || '기타',
                };
                const result = await addFavorite(newFav);
                setFavorites(prev => [...prev, result]);
                place.favoriteData = result;
            }
        } catch (e) {
            alert("즐겨찾기 변경 실패");
        }
    };

    const handleToggleVisited = async (place, planItem) => {
        try {
            const newChecked = !planItem.isChecked;
            const targetPlanId = planItem.planId;
            const targetPlan = plans.find(p => p.id === targetPlanId);
            if (!targetPlan) return;

            const planToUpdate = JSON.parse(JSON.stringify(targetPlan)); // Deep Clone

            let schedule = planToUpdate.plan ? planToUpdate.plan.schedule : planToUpdate.schedule;
            if (!schedule && planToUpdate.data && planToUpdate.data.schedule) schedule = planToUpdate.data.schedule;

            if (schedule && schedule[planItem.day - 1] && schedule[planItem.day - 1].places[planItem.placeIndex]) {
                schedule[planItem.day - 1].places[planItem.placeIndex].isChecked = newChecked;
            } else {
                return;
            }
            // API Call
            const body = planToUpdate.plan || planToUpdate;
            await updateAiPlan(planToUpdate.id, body);

            // Update State
            setPlans(prev => prev.map(p => p.id === targetPlanId ? planToUpdate : p));

            // Sync optimistic UI:
            planItem.isChecked = newChecked;
            // Also force update selectedPlace
            setSelectedPlace(prev => ({ ...prev }));

        } catch (e) {
            console.error(e);
            alert("방문 여부 저장 실패");
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button className={styles.backButton} onClick={() => navigate(-1)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 12H5" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 19L5 12L12 5" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            <KakaoMap
                center={resolvedPlaces.length > 0
                    ? { lat: Number(resolvedPlaces[0].lat), lng: Number(resolvedPlaces[0].lng) }
                    : { lat: 37.5665, lng: 126.9780 }}
                isPanto
                level={resolvedPlaces.length > 0 ? 8 : 4}
                className={styles.map}
                onClick={() => setSelectedPlace(null)}
            >
                {resolvedPlaces.map((place) => {
                    const isFav = !!place.favoriteData;
                    const isVisited = place.plansData.some(p => p.isChecked);

                    let markerImage = null;
                    if (isFav) {
                        markerImage = {
                            src: starMarkerIcon,
                            size: { width: 32, height: 32 },
                            options: { offset: { x: 16, y: 32 } }
                        };
                    } else if (isVisited) {
                        markerImage = {
                            src: visitedMarkerIcon,
                            size: { width: 32, height: 32 },
                            options: { offset: { x: 16, y: 32 } }
                        };
                    }

                    return (
                        <MapMarker
                            key={place.key}
                            position={{ lat: Number(place.lat), lng: Number(place.lng) }}
                            image={markerImage}
                            onClick={() => setSelectedPlace(place)}
                        />
                    );
                })}
            </KakaoMap>

            {selectedPlace && (
                <div className={styles.bottomSheet}>
                    <div className={styles.sheetHeader}>
                        <h3 className={styles.placeName}>{selectedPlace.name}</h3>
                        <div className={styles.favButton} onClick={() => handleToggleFavorite(selectedPlace)}>
                            {selectedPlace.favoriteData ? "★" : "☆"}
                        </div>
                    </div>
                    <p className={styles.address}>{selectedPlace.address}</p>

                    <div className={styles.plansList}>
                        <h4>관련 플랜</h4>
                        {selectedPlace.plansData.length === 0 && <p className={styles.noPlan}>플랜에 없는 장소입니다.</p>}
                        {selectedPlace.plansData.map((item, idx) => (
                            <div key={idx} className={styles.planItem}>
                                <span>{item.planTitle || `Unnamed Plan`} (Day {item.day})</span>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={item.isChecked}
                                        onChange={() => handleToggleVisited(selectedPlace, item)}
                                    />
                                    방문
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapPage;
